import {
    AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output
} from '@angular/core';
import {Store} from '@ngrx/store';
import VideoState from '../shared/stores/root-store/video.state';
import {Observable, Subscription, timer} from 'rxjs';
import {FrameService} from '../shared/services/frame.service';
import {VideoStateEnum} from '../../enums/video-state.enum';
import {BeginUpdateVideoTimeAction, UpdateCurrentFrameAction} from '../shared/stores/root-store/actions/video.action';
import {selectActualVideoState} from '../shared/stores/root-store/selectors/video.selector';
import {ActionService} from '../shared/services/action.service';

@Component({
    selector: 'eocm-frame-accurate',
    templateUrl: './frame-accurate.component.html'
})
export class FrameAccurateComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() player: HTMLVideoElement;
    @Input() currentVideo;
    @Input() jumpPerformed;
    @Output() jumpPerformedChange = new EventEmitter<boolean>();

    timer: Observable<number> = null;
    timerTickAction: Subscription = null;
    previousVideoTime = 0;
    previousClockTime = 0;
    pausedVideoTime = null;
    lastTimeUpdateTimestamp = null;
    currentFrame = 0;
    accurateUpdatesCount = 0;
    debug = false;
    currentVideoState = VideoStateEnum.Ended;
    lastPlayedFrame = 0;

    constructor(
        private store: Store<{ video: VideoState }>,
        private frameService: FrameService,
        private actionService: ActionService
    ) { }

    ngOnInit() {
        this.store.select(selectActualVideoState).subscribe(state => {
            this.currentVideoState = state;
        });
    }

    ngOnDestroy() {
        if (this.timerTickAction) {
            this.timerTickAction.unsubscribe();
        }
    }

    ngAfterViewInit() {
        this.initializeTimer();
    }

    initializeTimer(clockAccuracy: boolean = false) {
        if (this.timerTickAction) {
            this.timerTickAction.unsubscribe();
        }

        this.timer = timer(0, 1000 / this.frameService.FPS_NUMBER_CONST / 2);
        this.timerTickAction = this.timer.subscribe(() => {
            this.onTimerTick(clockAccuracy);
        });
    }

    getVideoTimeDiff(): number {
        return Math.round((this.player.currentTime - this.previousVideoTime) * 1000);
    }

    getClockTimeDiff(now: number): number {
        return this.previousClockTime
            ? now - this.previousClockTime
            : 0;
    }

    performPause(now: number) {
        this.pausedVideoTime = this.pausedVideoTime || this.player.currentTime;

        this.update(this.player.currentTime, now);
    }

    performVideoTimeUpdate(videoTimeDiff: number, clockTimeDiff: number, now: number) {
        this.pausedVideoTime = null;

        if (0 === videoTimeDiff) {
            this.update(this.player.currentTime + (clockTimeDiff / 1000), now);

            return;
        }

        this.update(this.player.currentTime, now);
        this.checkForFrameAccurateUpdates(videoTimeDiff, clockTimeDiff);
    }

    update(videoTime: number, clockTime?: number) {
        this.store.dispatch(BeginUpdateVideoTimeAction({
            payload: {
                currentTime: videoTime,
                currentSceneId: this.currentVideo.id
            }
        }));

        this.store.dispatch(UpdateCurrentFrameAction({
            currentFrame: this.frameService.getRoundFrame(videoTime)
        })
        );

        if (clockTime) {
            this.lastTimeUpdateTimestamp = clockTime;
            this.previousClockTime = clockTime;
        }
    }

    shouldUpdateCurrentVideoTime(): boolean {
        return null === this.pausedVideoTime || this.pausedVideoTime !== this.player.currentTime;
    }

    shouldHandlePause(): boolean {
        return this.currentVideoState === VideoStateEnum.Pause ||
      this.currentVideoState === VideoStateEnum.UserPause ||
      this.currentVideoState === VideoStateEnum.Interaction
        ;
    }

    checkForFrameAccurateUpdates(videoTimeDiff: number, clockTimeDiff: number) {
        if (Math.abs(videoTimeDiff - clockTimeDiff) <= 2) {
            if (++this.accurateUpdatesCount > 3) {
                this.initializeTimer(true);
            }
        } else {
            this.accurateUpdatesCount = 0;
        }
    }

    private onTimerTick(clockAccuracy: boolean) {
        if (
            !this.player ||
      this.player.seeking ||
      this.currentVideoState === VideoStateEnum.Buffering ||
      this.currentVideoState === VideoStateEnum.Ended
        ) {
            if (this.player?.seeking) {
                this.lastPlayedFrame = this.frameService.getRoundFrame(this.player.currentTime);
            }

            if (this.currentVideoState === VideoStateEnum.Ended && this.lastPlayedFrame > 0) {
                this.lastPlayedFrame = 0;
            }

            return;
        }

        if (this.jumpPerformed) {
            this.jumpPerformedChange.emit(false);
        } else {
            this.frameService.getFrameRange(this.previousVideoTime, this.player.currentTime)
                .map(frame => {
                    if (frame > this.lastPlayedFrame) {

                        this.lastPlayedFrame = frame;

                        if (this.actionService.checkAndPerformAction(frame)) {
                            this.lastPlayedFrame = this.frameService.getRoundFrame(this.player.currentTime);
                        }
                    }
                });
        }

        if (clockAccuracy) {
            this.update(this.player.currentTime);
        } else {
            const videoTimeDiff = this.getVideoTimeDiff();
            const now = (new Date()).getTime();
            const clockTimeDiff = this.getClockTimeDiff(now);

            if (this.shouldHandlePause()) {
                this.performPause(now);

            } else if (this.shouldUpdateCurrentVideoTime()) {
                this.performVideoTimeUpdate(videoTimeDiff, clockTimeDiff, now);
            }
        }

        this.currentFrame = this.frameService.getRoundFrame(this.player.currentTime);
        this.previousVideoTime = this.player.currentTime;
    }
}
